import { useMutation, useQueryClient } from "@tanstack/react-query";
import { decommaizeNumber } from "@toss/utils";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { CommonFacilityModel } from "src/api/building/building-types";
import {
  AvailableReservationTime,
  mutationAvailableReservationTimeAsync,
  mutationReservationPolicyModeAsync,
  ReservationPolicyMode,
} from "src/api/product/product-api";
import { BaseButton, BaseCheckbox, BaseInput, BaseRadio, BaseTooltip } from "src/components";
import useGetAvailableReservationTime from "src/hooks/product/useGetAvailableReservationTime";
import { SpaceTypeT } from "src/pages/product/product-types";
import { useModal } from "src/recoil/modalState/hook";
import { axiosInstance, commaizePrice, onlyNumber } from "src/utils";

import { useToast } from "src/recoil/toast/hook";
import _ from "lodash";
import useDirtyState from "src/recoil/dirty/hooks";
import useGetReservationPolicyMode from "src/hooks/product/useGetReservationPolicyMode";

export interface ReservationProps {
  spaceType: SpaceTypeT;
  serviceType: UnionServiceType;
  serviceId: string;

  isDefault: boolean; // 기본설정 or 개별설정 호출 방식 결정

  space?: CommonFacilityModel;
  isRemovable?: boolean;
  isEditable?: boolean;
}

interface AvailableReservationTimeForm {
  isLimitedAvailableReservationTime: boolean; // 예약가능시간대제한여부

  isAvailableReservationTimeStart: boolean;
  isAvailableReservationTimeEnd: boolean;

  availableReservationTimeStart: string; // 예약가능시간대시작 (단위: 분)
  availableReservationTimeEnd: string; // 예약가능시간대종료

  // 개별 input validation을 위한 hidden fields
  startHour: string;
  startMinute: string;
  endHour: string;
  endMinute: string;

  isPolicyMode?: boolean;
}

const defaultValues = {
  isLimitedAvailableReservationTime: false,

  isAvailableReservationTimeStart: false,
  isAvailableReservationTimeEnd: false,

  availableReservationTimeStart: "", // 예약가능시간대시작 (단위: 분)
  availableReservationTimeEnd: "", // 예약가능시간대종료

  // 개별 input validation을 위한 hidden fields
  startHour: "",
  startMinute: "",
  endHour: "",
  endMinute: "",
};

const ReservationAvailabilityPeriod = ({ spaceType, serviceType, serviceId, isDefault, space, isEditable }: ReservationProps) => {
  const { setBaseModal, setAbstractModalZ1 } = useModal();
  const queryClient = useQueryClient();
  const { openToast } = useToast();
  const [availableTimeStart, setAvailableTimeStart] = useState<{ day: string; hour: string; minute: string }>({
    day: "",
    hour: "",
    minute: "",
  });
  const [availableTimeEnd, setAvailableTimeEnd] = useState<{ day: string; hour: string; minute: string }>({
    day: "",
    hour: "",
    minute: "",
  });

  const { mutateAsync: mutationReservationPolicyMode } = useMutation({
    mutationFn: (data: ReservationPolicyMode[]) => mutationReservationPolicyModeAsync(axiosInstance, data),
    onSuccess: (data) => {
      console.log("mutationReservationPolicyModeAsync data :>> ", data);
      queryClient.invalidateQueries({
        queryKey: ["getReservationPolicyModeAsync"],
      });
    },
  });

  // harry 수정 > v.1.28.1 에 반영
  // const renderText = (isLimit: boolean, time: string) => {
  //   if (isLimit) {
  //     return time === "" || time === "0" ? "분 (0시간 0분)" : `분 (${convertMinutesToDHMS(deleteCommas(time))})`;
  //   } else {
  //     return;
  //   }
  // };

  const { data: availableReservationTimeList } = useGetAvailableReservationTime({
    AvailableReservationTimeListParams: {
      isDefault: isDefault,
      serviceId,
      serviceType,
      commonFacilityType: spaceType,
      ...(space?.id && {
        buildingCommonFacilityId: String(space?.id),
      }),
    },
  });

  const isRegisterMode = !availableReservationTimeList?.[0]?.id;

  const { mutate } = useMutation({
    mutationFn: (data: AvailableReservationTime[]) => mutationAvailableReservationTimeAsync(axiosInstance, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getAvailableReservationTimeAsync"],
      });
    },
  });

  const { data: reservationPolicyModeList } = useGetReservationPolicyMode({
    reservationPolicyModeListParams: {
      serviceType,
      serviceId,
      commonFacilityType: spaceType,
      buildingCommonFacilityId: space?.id?.toString(),
      reservationPolicyModeType: "RESERVATION_POLICY_MODE_AVAILABLE",
    },
  });

  const reservationPolicyMode = reservationPolicyModeList?.find(
    (data) => String(data.buildingCommonFacilityId) === String(space?.id) && data.reservationPolicyModeType === "RESERVATION_POLICY_MODE_AVAILABLE",
  );

  const [isPolicyMode, setIsPolicyMode] = useState<boolean>();

  useEffect(() => {
    // props isDefault가 false 인개별 설정 페이지에서 reservationPolicyMode의 isDefault 값으로 PolicyMode 값을 사용함(null 또는 undefined 면 true로 전환)
    setIsPolicyMode(!isDefault ? reservationPolicyMode?.isDefault !== false : false);
  }, [isDefault, reservationPolicyMode, reservationPolicyModeList, setIsPolicyMode, space?.id]);

  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
    register,
    unregister,
    watch,
    trigger,
    control,
    setError,
    clearErrors,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<AvailableReservationTimeForm>({
    defaultValues: defaultValues,
  });

  // 마지막으로 변경된 필드 추적
  const lastChangedFieldRef = useRef<"startHour" | "startMinute" | "endHour" | "endMinute" | null>(null);

  const { setIsDirtyState, isDirtyState } = useDirtyState();

  useEffect(() => {
    if (isDirty) {
      return setIsDirtyState(true);
    } else if (!_.isEqual(!isDefault ? reservationPolicyMode?.isDefault !== false : false, isPolicyMode)) {
      return setIsDirtyState(true);
    } else {
      setIsDirtyState(false);
    }
  }, [dirtyFields, isDefault, isDirty, isPolicyMode, reservationPolicyMode?.isDefault, setIsDirtyState]);

  /** 시간/분 validation */
  const startHourValue = watch("startHour");
  const startMinuteValue = watch("startMinute");
  const endHourValue = watch("endHour");
  const endMinuteValue = watch("endMinute");

  useEffect(() => {
    const lastChanged = lastChangedFieldRef.current;

    // 마지막으로 변경된 필드의 에러를 우선 체크
    if (lastChanged === "startHour" && parseInt(startHourValue) > 23) {
      setError("startHour", { type: "manual", message: "시간은 23까지 입력 가능합니다" });
      clearErrors(["startMinute", "endHour", "endMinute"]);
      return;
    }

    if (lastChanged === "startMinute" && parseInt(startMinuteValue) > 59) {
      setError("startMinute", { type: "manual", message: "분은 59까지 입력 가능합니다" });
      clearErrors(["startHour", "endHour", "endMinute"]);
      return;
    }

    if (lastChanged === "endHour" && parseInt(endHourValue) > 23) {
      setError("endHour", { type: "manual", message: "시간은 23까지 입력 가능합니다" });
      clearErrors(["startHour", "startMinute", "endMinute"]);
      return;
    }

    if (lastChanged === "endMinute" && parseInt(endMinuteValue) > 59) {
      setError("endMinute", { type: "manual", message: "분은 59까지 입력 가능합니다" });
      clearErrors(["startHour", "startMinute", "endHour"]);
      return;
    }

    // 그 외의 경우 순차적으로 체크
    if (parseInt(startHourValue) > 23) {
      setError("startHour", { type: "manual", message: "시간은 23까지 입력 가능합니다" });
      clearErrors(["startMinute", "endHour", "endMinute"]);
      return;
    }

    if (parseInt(startMinuteValue) > 59) {
      setError("startMinute", { type: "manual", message: "분은 59까지 입력 가능합니다" });
      clearErrors(["startHour", "endHour", "endMinute"]);
      return;
    }

    if (parseInt(endHourValue) > 23) {
      setError("endHour", { type: "manual", message: "시간은 23까지 입력 가능합니다" });
      clearErrors(["startHour", "startMinute", "endMinute"]);
      return;
    }

    if (parseInt(endMinuteValue) > 59) {
      setError("endMinute", { type: "manual", message: "분은 59까지 입력 가능합니다" });
      clearErrors(["startHour", "startMinute", "endHour"]);
      return;
    }

    clearErrors(["startHour", "startMinute", "endHour", "endMinute"]);
  }, [startHourValue, startMinuteValue, endHourValue, endMinuteValue, setError, clearErrors]);

  const onCloseTabModal = () => {
    if (isDirtyState) {
      setBaseModal({
        isOpen: true,
        title: "취소하시겠습니까?",
        children: "저장하지 않고 취소하면 입력한 내용이 모두 삭제됩니다.",
        btnLeftTitle: "취소",
        btnRightTitle: "확인",
        onClick: () => {
          setIsDirtyState(false);
          setBaseModal({ isOpen: false });
          setAbstractModalZ1({ isOpen: false });
        },
        onClose: () => {
          setBaseModal({ isOpen: false });
        },
      });
    } else {
      setIsDirtyState(false);
      setAbstractModalZ1({ isOpen: false });
    }
  };

  useEffect(() => {
    const defaultSettingData = availableReservationTimeList?.[0];

    if (defaultSettingData) {
      //서버에서 내려온 데이터가 있으면(수정모드일때)

      // -1 값 처리: -1이면 빈 값으로 처리
      const startValue = defaultSettingData?.availableReservationTimeStart;
      const endValue = defaultSettingData?.availableReservationTimeEnd;

      // 시작 시간을 일, 시간, 분으로 분해 (-1이면 0으로 처리)
      const startTotalMinutes = startValue === -1 ? 0 : startValue || 0;
      const startDays = startValue === -1 ? 0 : Math.floor(startTotalMinutes / (24 * 60));
      const startHours = startValue === -1 ? 0 : Math.floor((startTotalMinutes % (24 * 60)) / 60);
      const startMinutes = startValue === -1 ? 0 : startTotalMinutes % 60;

      // 종료 시간을 일, 시간, 분으로 분해 (-1이면 0으로 처리)
      const endTotalMinutes = endValue === -1 ? 0 : endValue || 0;
      const endDays = endValue === -1 ? 0 : Math.floor(endTotalMinutes / (24 * 60));
      const endHours = endValue === -1 ? 0 : Math.floor((endTotalMinutes % (24 * 60)) / 60);
      const endMinutes = endValue === -1 ? 0 : endTotalMinutes % 60;

      // state 설정 (-1이면 모든 값을 빈 문자열로)
      setAvailableTimeStart({
        day: startValue === -1 || startDays === 0 ? "" : startDays.toString(),
        hour: startValue === -1 || startHours === 0 ? "" : startHours.toString(),
        minute: startValue === -1 || startMinutes === 0 ? "" : startMinutes.toString(),
      });

      setAvailableTimeEnd({
        day: endValue === -1 || endDays === 0 ? "" : endDays.toString(),
        hour: endValue === -1 || endHours === 0 ? "" : endHours.toString(),
        minute: endValue === -1 || endMinutes === 0 ? "" : endMinutes.toString(),
      });

      reset({
        isLimitedAvailableReservationTime: defaultSettingData?.isLimitedAvailableReservationTime,

        // -1이면 false, 그 외에는 값이 있으면 true
        isAvailableReservationTimeStart: startValue !== -1 && !!commaizePrice(startValue),
        isAvailableReservationTimeEnd: endValue !== -1 && !!commaizePrice(endValue),

        // -1이면 빈 문자열, 그 외에는 값 표시
        availableReservationTimeStart: startValue === -1 ? "" : commaizePrice(startValue),
        availableReservationTimeEnd: endValue === -1 ? "" : commaizePrice(endValue),

        // hidden field 초기화 (validation용)
        startHour: startValue === -1 || startHours === 0 ? "" : startHours.toString(),
        startMinute: startValue === -1 || startMinutes === 0 ? "" : startMinutes.toString(),
        endHour: endValue === -1 || endHours === 0 ? "" : endHours.toString(),
        endMinute: endValue === -1 || endMinutes === 0 ? "" : endMinutes.toString(),
      });
    }
  }, [availableReservationTimeList, reset]);

  const onValid = async (data: AvailableReservationTimeForm) => {
    if (!isDefault) {
      //개별설정일때
      await mutationReservationPolicyMode([
        {
          serviceId: Number(serviceId), // 서비스ID
          serviceType: serviceType, // 서비스타입
          commonFacilityType: spaceType, // 공용공간타입
          isDefault: isPolicyMode, // 기본설정여부

          buildingCommonFacilityId: Number(space?.id), // 건물-공용공간 ID (isDefault=false인 경우 필수)

          reservationPolicyModeType: "RESERVATION_POLICY_MODE_AVAILABLE",

          cmdType: reservationPolicyMode?.id ? "U" : "C", // 요청타입
          ...(reservationPolicyMode?.id && {
            // 수정모드일때 id 등록
            id: reservationPolicyMode?.id, // 예약가능시간대 ID
          }),
        },
      ]);
    }

    const sendData: AvailableReservationTime[] = [
      {
        serviceId: Number(serviceId), // 서비스ID
        serviceType: serviceType, // 서비스타입
        commonFacilityType: spaceType, // 공용공간타입
        isDefault: isDefault, // 기본설정여부

        isLimitedAvailableReservationTime: data.isLimitedAvailableReservationTime, // 예약가능시간대제한여부
        availableReservationTimeStart: data.availableReservationTimeStart === "" ? -1 : decommaizeNumber(data.availableReservationTimeStart!), // 예약가능시간대시작 (단위: 분)
        availableReservationTimeEnd: data.availableReservationTimeEnd === "" ? -1 : decommaizeNumber(data.availableReservationTimeEnd!), // 예약가능시간대종료
        cmdType: isRegisterMode ? "C" : "U", // 요청타입
        buildingCommonFacilityId: Number(space?.id), // 건물-공용공간 ID (isDefault=false인 경우 필수)

        ...(!isRegisterMode && {
          // 수정모드일때 id 등록
          id: availableReservationTimeList?.[0]?.id, // 예약가능시간대 ID
        }),
      },
    ];

    console.log("Available sendData :>> ", sendData);

    mutate(sendData, {
      onSuccess(data, variables, context) {
        console.log("onSuccess data :>> ", data);
        setBaseModal({ isOpen: false });
        openToast({ content: "저장 되었습니다.", isSystemToast: true });
        setIsDirtyState(false);
      },
    });
  };

  //  유효성 확인 후 저장 진행
  const formValidation = () => {
    if (watch("isLimitedAvailableReservationTime") === true && isPolicyMode === false) {
      //제한이면서 개별설정으로 선택시 벨리데이션 체크함
      //무제한과 기본설정에서는 벨리데이션 안함

      // 개별 input validation 등록
      register(`startHour`, {
        validate: (value) => {
          if (value && parseInt(value) > 23) {
            return "23시간까지 입력 가능합니다";
          }
          return true;
        },
      });

      register(`startMinute`, {
        validate: (value) => {
          if (value && parseInt(value) > 59) {
            return "59분까지 입력 가능합니다.";
          }
          return true;
        },
      });

      register(`endHour`, {
        validate: (value) => {
          if (value && parseInt(value) > 23) {
            return "23시간까지 입력 가능합니다";
          }
          return true;
        },
      });

      register(`endMinute`, {
        validate: (value) => {
          if (value && parseInt(value) > 59) {
            return "59분까지 입력 가능합니다.";
          }
          return true;
        },
      });

      register(`availableReservationTimeStart`, {
        validate: (value) => {
          // 시작 시간보다 큰지 체크는 기존 로직 유지
          return true;
        },
      });

      register(`availableReservationTimeEnd`, {
        validate: (value) => {
          if (value) {
            console.log("value", value);
            // 시작 시간보다 큰지 체크
            if (watch("availableReservationTimeStart") && value !== "0") {
              if (decommaizeNumber(value) <= decommaizeNumber(watch("availableReservationTimeStart"))) {
                return "시작 시간을 종료 시간 이전으로 입력해 주세요.";
              }
            }
          }
          return true;
        },
      });

      register(`isAvailableReservationTimeStart`, {
        validate: (value) => {
          if (value === false && watch("isAvailableReservationTimeEnd") === false) {
            return "예약 가능 시작 시간 또는 예약 가능 종료 시간 중 하나를 반드시 선택해주세요";
          }
          const timeValue = watch("availableReservationTimeStart");
          if (value === true && (timeValue === "" || timeValue === "0")) {
            return "시간을 입력해 주세요.";
          }

          return true;
        },
      });

      register(`isAvailableReservationTimeEnd`, {
        validate: (value) => {
          if (value === false && watch("isAvailableReservationTimeStart") === false) {
            return "예약 가능 시작 시간 또는 예약 가능 종료 시간 중 하나를 반드시 선택해주세요";
          }
          const timeValue = watch("availableReservationTimeEnd");
          if (value === true && (timeValue === "" || timeValue === "0")) {
            return "시간을 입력해 주세요.";
          }
          return true;
        },
      });
    } else {
      register("startHour", {
        validate: () => true,
      });
      register("startMinute", {
        validate: () => true,
      });
      register("endHour", {
        validate: () => true,
      });
      register("endMinute", {
        validate: () => true,
      });
      register("availableReservationTimeStart", {
        validate: () => true,
      });
      register("availableReservationTimeEnd", {
        validate: () => true,
      });
      register("isAvailableReservationTimeStart", {
        validate: () => true,
      });
      register("isAvailableReservationTimeEnd", {
        validate: () => true,
      });
    }
  };

  formValidation();

  return (
    <>
      <section className="base-abstract-modal__contents px30">
        <form onSubmit={handleSubmit(onValid)}>
          {!isDefault && (
            <>
              <div className="base-abstract-modal__contents-subtitle">
                <span className="required font18 font-weight-semibold">예약 정책 적용 방식</span>
              </div>
              <div className="flex-center h36 pt10">
                <BaseRadio
                  id={"isPolicyMode_true"}
                  name={"isPolicyMode"}
                  checked={isPolicyMode === true}
                  label="기본설정"
                  onChange={() => {
                    setIsPolicyMode(true);
                  }}
                />
                <BaseTooltip
                  className={"mr10 no-wrap"}
                  tooltip={
                    (
                      <div>
                        <div>• 예약 기본 정책 설정을 그대로 따릅니다.</div>
                        <div>• 기본 설정이 변경되면 즉시 적용됩니다.</div>
                      </div>
                    ) as any as string
                  }
                  touchIcon="QUESTION"
                ></BaseTooltip>
                <BaseRadio
                  id={"isPolicyMode_false"}
                  name={"isPolicyMode"}
                  checked={isPolicyMode === false}
                  label="개별설정" // 무제한
                  onChange={() => {
                    setIsPolicyMode(false);
                  }}
                />
                <BaseTooltip
                  tooltip={
                    (
                      <div>
                        <div>• 예약 기본 정책 설정을 따르지 않고 이 공용 공간에만 적용되는 별도의 예약 정책을 설정합니다.</div>
                        <div>• 기본 설정이 변경되더라도 영향을 받지 않습니다.</div>
                      </div>
                    ) as any as string
                  }
                  touchIcon="QUESTION"
                ></BaseTooltip>
              </div>
            </>
          )}

          <div className="base-abstract-modal__contents-subtitle">
            <span className="font18 font-weight-semibold">예약 가능 기간 제한</span>
          </div>
          <div className="flex-column d-flex">
            <span className="font14 text-primary3 pb10">
              • 현재 시점부터 선택 가능한 예약 기간을 제한할 수 있습니다. (예: 2시간 이후부터 48시간 이내 예약 가능)
            </span>

            <Controller
              control={control}
              name={"isLimitedAvailableReservationTime"}
              render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                <div className="flex-center h36 pt10">
                  <BaseRadio
                    id={"isLimitedAvailableReservationTime_false"}
                    name={name}
                    checked={
                      //개별설정 && 첫 저장전(빈배열) 이면 false 아니면 값에 따라 결정
                      isPolicyMode && Array.isArray(availableReservationTimeList) && availableReservationTimeList.length === 0
                        ? false
                        : value === false
                    }
                    label="무제한" // 무제한
                    onChange={() => {
                      onChange(false);
                    }} // send value to hook form
                    className={"mr10"}
                    disabled={isPolicyMode !== false}
                  />
                  <BaseRadio
                    id={"isLimitedAvailableReservationTime_true"}
                    name={name}
                    checked={value === true}
                    label="제한"
                    disabled={isPolicyMode !== false}
                    onChange={() => {
                      onChange(true);

                      //첫 설정시 사용함 체크
                      if (isRegisterMode) {
                        setValue("isAvailableReservationTimeStart", true, {
                          shouldDirty: true,
                        });
                        setValue("isAvailableReservationTimeEnd", true, {
                          shouldDirty: true,
                        });
                      }
                    }} // send value to hook form
                  />
                </div>
              )}
            ></Controller>
          </div>

          <div className="base-abstract-modal__contents-subtitle">
            <span className="font18 font-weight-semibold">예약 가능 시작 시간</span>
          </div>
          <div className="flex-column d-flex">
            <span className="font14 text-primary3">• 현재 시점으로부터 설정한 시간 이후에 예약을 할 수 있습니다.</span>
            <div className="flex-center pt10">
              <Controller
                control={control}
                name={"isAvailableReservationTimeStart"}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  <BaseCheckbox
                    className="w-fit mr10"
                    id={name}
                    name={name}
                    checked={value}
                    onChange={(checked) => {
                      onChange(checked);
                      setValue("availableReservationTimeStart", "", { shouldDirty: true });
                      setAvailableTimeStart({ day: "", hour: "", minute: "" });
                    }}
                    label="적용함"
                    disabled={watch("isLimitedAvailableReservationTime") === false || isPolicyMode !== false}
                  />
                )}
              ></Controller>

              <div className="flex-center">
                <div className="minmax160">
                  <BaseInput
                    //2,147,483,647
                    type="text"
                    value={availableTimeStart.day}
                    placeholder="일"
                    maxLength={4}
                    onChange={(text: string) => {
                      const day = onlyNumber(text);
                      const newTimeStart = { ...availableTimeStart, day: day };
                      setAvailableTimeStart(newTimeStart);

                      // 총 분으로 환산하여 form field 업데이트
                      const totalMinutes =
                        (parseInt(newTimeStart.day) || 0) * 24 * 60 + (parseInt(newTimeStart.hour) || 0) * 60 + (parseInt(newTimeStart.minute) || 0);
                      setValue("availableReservationTimeStart", commaizePrice(totalMinutes.toString()));
                    }}
                    disabled={
                      watch("isLimitedAvailableReservationTime") === false ||
                      watch("isAvailableReservationTimeStart") === false ||
                      isPolicyMode !== false
                    }
                  />
                </div>
                <span className="font14 mx10">일</span>

                <div className="minmax160">
                  <Controller
                    control={control}
                    name="startHour"
                    render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                      <BaseInput
                        type="text"
                        value={value}
                        maxLength={2}
                        placeholder="시간"
                        onChange={(text: string) => {
                          lastChangedFieldRef.current = "startHour";
                          const hour = onlyNumber(text);
                          onChange(hour);
                          const newTimeStart = { ...availableTimeStart, hour: hour };
                          setAvailableTimeStart(newTimeStart);

                          // 총 분으로 환산하여 form field 업데이트
                          const totalMinutes =
                            (parseInt(newTimeStart.day) || 0) * 24 * 60 +
                            (parseInt(newTimeStart.hour) || 0) * 60 +
                            (parseInt(newTimeStart.minute) || 0);
                          setValue("availableReservationTimeStart", commaizePrice(totalMinutes.toString()));
                        }}
                        disabled={
                          watch("isLimitedAvailableReservationTime") === false ||
                          watch("isAvailableReservationTimeStart") === false ||
                          isPolicyMode !== false
                        }
                      />
                    )}
                  />
                </div>
                <span className="font14 mx10">시간</span>
                <div className="minmax160">
                  <Controller
                    control={control}
                    name="startMinute"
                    render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                      <BaseInput
                        type="text"
                        value={value}
                        maxLength={2}
                        placeholder="분"
                        onChange={(text: string) => {
                          lastChangedFieldRef.current = "startMinute";
                          const minute = onlyNumber(text);
                          onChange(minute);
                          const newTimeStart = { ...availableTimeStart, minute: minute };
                          setAvailableTimeStart(newTimeStart);

                          // 총 분으로 환산하여 form field 업데이트
                          const totalMinutes =
                            (parseInt(newTimeStart.day) || 0) * 24 * 60 +
                            (parseInt(newTimeStart.hour) || 0) * 60 +
                            (parseInt(newTimeStart.minute) || 0);
                          setValue("availableReservationTimeStart", commaizePrice(totalMinutes.toString()));
                        }}
                        disabled={
                          watch("isLimitedAvailableReservationTime") === false ||
                          watch("isAvailableReservationTimeStart") === false ||
                          isPolicyMode !== false
                        }
                      />
                    )}
                  />
                </div>
                <span className="font14 mx10">분 이후부터</span>
              </div>
            </div>
            {(errors.startHour?.message || errors.startMinute?.message) && (
              <p className="validation-text">{errors.startHour?.message || errors.startMinute?.message}</p>
            )}
            {errors.isAvailableReservationTimeStart?.message && (
              <p className="validation-text">{errors.isAvailableReservationTimeStart?.message ?? ""}</p>
            )}
            {errors.availableReservationTimeStart?.message && (
              <p className="validation-text">{errors.availableReservationTimeStart?.message ?? ""}</p>
            )}
          </div>
          <div className="base-abstract-modal__contents-subtitle">
            <span className="font18 font-weight-semibold">예약 가능 종료 시간</span>
          </div>
          <div className="flex-column d-flex">
            <span className="font14 text-primary3">• 현재 시점으로부터 설정한 시간 이내에 예약을 할 수 있습니다.</span>
            <div className="flex-center pt10">
              <Controller
                control={control}
                name={"isAvailableReservationTimeEnd"}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  <BaseCheckbox
                    className="w-fit mr10"
                    id={name}
                    name={name}
                    checked={value}
                    onChange={(checked) => {
                      onChange(checked);
                      setValue("availableReservationTimeEnd", "", { shouldDirty: true });
                      setAvailableTimeEnd({ day: "", hour: "", minute: "" });
                    }}
                    label="적용함"
                    disabled={watch("isLimitedAvailableReservationTime") === false || isPolicyMode !== false}
                  />
                )}
              ></Controller>

              <div className="flex-center">
                <div className="minmax160">
                  <BaseInput
                    //2,147,483,647
                    type="number"
                    value={availableTimeEnd.day}
                    placeholder="일"
                    maxLength={4}
                    onChange={(text: string) => {
                      const day = onlyNumber(text);
                      const newTimeEnd = { ...availableTimeEnd, day: day };
                      setAvailableTimeEnd(newTimeEnd);

                      // 총 분으로 환산하여 form field 업데이트
                      const totalMinutes =
                        (parseInt(newTimeEnd.day) || 0) * 24 * 60 + (parseInt(newTimeEnd.hour) || 0) * 60 + (parseInt(newTimeEnd.minute) || 0);
                      setValue("availableReservationTimeEnd", commaizePrice(totalMinutes.toString()));
                    }}
                    disabled={
                      watch("isLimitedAvailableReservationTime") === false ||
                      watch("isAvailableReservationTimeEnd") === false ||
                      isPolicyMode !== false
                    }
                  />
                </div>
                <span className="font14 mx10">일</span>

                <div className="minmax160">
                  <Controller
                    control={control}
                    name="endHour"
                    render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                      <BaseInput
                        type="text"
                        value={value}
                        maxLength={2}
                        placeholder="시간"
                        onChange={(text: string) => {
                          lastChangedFieldRef.current = "endHour";
                          const hour = onlyNumber(text);
                          onChange(hour);
                          const newTimeEnd = { ...availableTimeEnd, hour: hour };
                          setAvailableTimeEnd(newTimeEnd);

                          // 총 분으로 환산하여 form field 업데이트
                          const totalMinutes =
                            (parseInt(newTimeEnd.day) || 0) * 24 * 60 + (parseInt(newTimeEnd.hour) || 0) * 60 + (parseInt(newTimeEnd.minute) || 0);
                          setValue("availableReservationTimeEnd", commaizePrice(totalMinutes.toString()));
                        }}
                        disabled={
                          watch("isLimitedAvailableReservationTime") === false ||
                          watch("isAvailableReservationTimeEnd") === false ||
                          isPolicyMode !== false
                        }
                      />
                    )}
                  />
                </div>
                <span className="font14 mx10">시간</span>
                <div className="minmax160">
                  <Controller
                    control={control}
                    name="endMinute"
                    render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                      <BaseInput
                        type="text"
                        value={value}
                        maxLength={2}
                        placeholder="분"
                        onChange={(text: string) => {
                          lastChangedFieldRef.current = "endMinute";
                          const minute = onlyNumber(text);
                          onChange(minute);
                          const newTimeEnd = { ...availableTimeEnd, minute: minute };
                          setAvailableTimeEnd(newTimeEnd);

                          // 총 분으로 환산하여 form field 업데이트
                          const totalMinutes =
                            (parseInt(newTimeEnd.day) || 0) * 24 * 60 + (parseInt(newTimeEnd.hour) || 0) * 60 + (parseInt(newTimeEnd.minute) || 0);
                          setValue("availableReservationTimeEnd", commaizePrice(totalMinutes.toString()));
                        }}
                        disabled={
                          watch("isLimitedAvailableReservationTime") === false ||
                          watch("isAvailableReservationTimeEnd") === false ||
                          isPolicyMode !== false
                        }
                      />
                    )}
                  />
                </div>
                <span className="font14 mx10">분 까지</span>
              </div>
            </div>
            {(errors.endHour?.message || errors.endMinute?.message) && (
              <p className="validation-text">{errors.endHour?.message || errors.endMinute?.message}</p>
            )}
            {errors.isAvailableReservationTimeEnd?.message && (
              <p className="validation-text">{errors.isAvailableReservationTimeEnd?.message ?? ""}</p>
            )}
            {errors.availableReservationTimeEnd?.message && <p className="validation-text">{errors.availableReservationTimeEnd?.message ?? ""}</p>}
          </div>
        </form>
      </section>

      <section className="base-abstract-modal__btn-wrap">
        {/* {checkChange && <span className="font14 mr10 text-red">*수정된 사항이 있으니 다시 한번 확인해주세요.</span>} */}
        <BaseButton
          title={"취소"}
          className="color-white"
          onClick={() => {
            onCloseTabModal();
          }}
        />
        {isEditable && (
          <BaseButton
            title={"저장"}
            onClick={async () => {
              (await trigger()) &&
                setBaseModal({
                  isOpen: true,
                  btnRightTitle: "확인",
                  btnLeftTitle: "취소",
                  title: "저장하시겠습니까?",
                  children: "저장 후에는 변경 사항이 즉시 적용됩니다.",
                  onClick: () => {
                    handleSubmit(onValid)();
                  },
                });
            }}
          />
        )}
      </section>
    </>
  );
};

export default ReservationAvailabilityPeriod;
